import React, { useRef, useEffect, useCallback } from 'react';
import { Umbrella, Waves, Sun, Sailboat, Palmtree, Heart, Sparkles, Sandwich, CupSoda, Pizza, Donut, IceCream, Drumstick, Scissors, Brush, SprayCan, Flower2, Dumbbell, Bike, Activity, HeartPulse, Timer, Footprints, Music, Headphones, Radio, Mic2, Disc3, Volume2, Dog, Cat, Bone, Fish, Bird, Bug, Wifi, Share2, Globe2, RadioTower, Smartphone, AtSign } from 'lucide-react';

const hexToRgb = (hex) => {
  const normalized = String(hex || '').replace('#', '');
  const value = /^[0-9a-fA-F]{6}$/.test(normalized) ? normalized : '0891b2';
  return [parseInt(value.slice(0, 2), 16), parseInt(value.slice(2, 4), 16), parseInt(value.slice(4, 6), 16)];
};
const THEMED_EFFECTS = {
  beach: [Umbrella, Waves, Sun, Sailboat, Palmtree],
  hearts: [Heart, HeartPulse, Sparkles],
  snacks: [Sandwich, CupSoda, Pizza, Donut, IceCream, Drumstick],
  beauty: [Sparkles, Scissors, Brush, SprayCan, Flower2],
  fit: [Dumbbell, Bike, Activity, HeartPulse, Timer, Footprints],
  music: [Music, Headphones, Radio, Mic2, Disc3, Volume2],
  pet: [Dog, Cat, Bone, Fish, Bird, Bug],
  internet: [Wifi, Share2, Globe2, RadioTower, Smartphone, AtSign],
};

/**
 * BackgroundEffect — animated canvas/CSS overlay for card backgrounds.
 *
 * Props:
 *   effect   — 'none' | 'stars' | 'geometric' | 'network' | 'modern' | 'bubbles' | 'rain' | 'orbits'
 *   darkMode — boolean
 *   contained — boolean  (true = absolute inside container, false = fixed fullscreen)
 */
export default function BackgroundEffect({ effect = 'none', darkMode = false, contained = false, color = '#0891b2', opacity = 60 }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  // ─── Canvas effects ────────────────────────────────────────────────────────
  const startCanvas = useCallback((canvas, type) => {
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rgb = hexToRgb(color);
    const intensity = Math.min(1, Math.max(0.3, Number(opacity || 60) / 100));
    const rgba = (alpha) => `rgba(${rgb.join(',')},${Math.min(1, Math.max(0, alpha * intensity))})`;
    let W = canvas.width;
    let H = canvas.height;

    const resize = () => {
      const r = canvas.getBoundingClientRect();
      canvas.width  = r.width  || window.innerWidth;
      canvas.height = r.height || window.innerHeight;
      W = canvas.width;
      H = canvas.height;
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Stars ────────────────────────────────────────────────────────────────
    if (type === 'stars') {
      const COUNT = Math.floor((W * H) / 8000) + 60;
      const stars = Array.from({ length: COUNT }, () => ({
        x:       Math.random() * W,
        y:       Math.random() * H,
        r:       Math.random() * 1.6 + 0.3,
        speed:   Math.random() * 0.18 + 0.04,
        angle:   Math.random() * Math.PI * 2,
        phase:   Math.random() * Math.PI * 2,
        twinkle: Math.random() * 0.04 + 0.01,
      }));

      const draw = (t) => {
        ctx.clearRect(0, 0, W, H);
        for (const s of stars) {
          const alpha = 0.35 + 0.55 * Math.abs(Math.sin(t * s.twinkle + s.phase));
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
          ctx.fillStyle = rgba(alpha);
          ctx.fill();

          // Drift slowly
          s.x += Math.cos(s.angle) * s.speed;
          s.y += Math.sin(s.angle) * s.speed;
          if (s.x < -2) s.x = W + 2;
          if (s.x > W + 2) s.x = -2;
          if (s.y < -2) s.y = H + 2;
          if (s.y > H + 2) s.y = -2;
        }
        animRef.current = requestAnimationFrame(draw);
      };
      animRef.current = requestAnimationFrame(draw);
    }

    // ── Geometric ────────────────────────────────────────────────────────────
    if (type === 'geometric') {
      const shapes = Array.from({ length: 14 }, (_, i) => ({
        x:     Math.random() * W,
        y:     Math.random() * H,
        sides: [3, 4, 5, 6][Math.floor(Math.random() * 4)],
        size:  Math.random() * 40 + 18,
        rot:   Math.random() * Math.PI * 2,
        dRot:  (Math.random() - 0.5) * 0.008,
        vx:    (Math.random() - 0.5) * 0.4,
        vy:    (Math.random() - 0.5) * 0.4,
        alpha: Math.random() * 0.12 + 0.04,
      }));

      const poly = (x, y, sides, size, rot) => {
        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
          const a = rot + (i / sides) * Math.PI * 2;
          const px = x + size * Math.cos(a);
          const py = y + size * Math.sin(a);
          i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.closePath();
      };

      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        for (const s of shapes) {
          ctx.save();
          poly(s.x, s.y, s.sides, s.size, s.rot);
          ctx.strokeStyle = rgba(s.alpha * 2.2);
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.restore();

          s.rot += s.dRot;
          s.x   += s.vx;
          s.y   += s.vy;
          if (s.x < -80)   s.x = W + 80;
          if (s.x > W + 80) s.x = -80;
          if (s.y < -80)   s.y = H + 80;
          if (s.y > H + 80) s.y = -80;
        }
        animRef.current = requestAnimationFrame(draw);
      };
      animRef.current = requestAnimationFrame(draw);
    }

    // ── Network ──────────────────────────────────────────────────────────────
    if (type === 'network') {
      const N = Math.floor((W * H) / 18000) + 30;
      const LINK_DIST = Math.min(W, H) * 0.22;
      const nodes = Array.from({ length: N }, () => ({
        x:  Math.random() * W,
        y:  Math.random() * H,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        r:  Math.random() * 2 + 1.5,
      }));


      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        // Links
        for (let i = 0; i < nodes.length; i++) {
          for (let j = i + 1; j < nodes.length; j++) {
            const dx = nodes[i].x - nodes[j].x;
            const dy = nodes[i].y - nodes[j].y;
            const d  = Math.sqrt(dx * dx + dy * dy);
            if (d < LINK_DIST) {
              const alpha = (1 - d / LINK_DIST) * 0.3;
              ctx.beginPath();
              ctx.moveTo(nodes[i].x, nodes[i].y);
              ctx.lineTo(nodes[j].x, nodes[j].y);
              ctx.strokeStyle = rgba(alpha);
              ctx.lineWidth = 0.8;
              ctx.stroke();
            }
          }
        }
        // Dots
        for (const n of nodes) {
          ctx.beginPath();
          ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
          ctx.fillStyle = rgba(0.55);
          ctx.fill();
          n.x += n.vx;
          n.y += n.vy;
          if (n.x < 0 || n.x > W) n.vx *= -1;
          if (n.y < 0 || n.y > H) n.vy *= -1;
        }
        animRef.current = requestAnimationFrame(draw);
      };
      animRef.current = requestAnimationFrame(draw);
    }

    // ── Bubbles ──────────────────────────────────────────────────────────────
    if (type === "bubbles") {
      const bubbles = Array.from({ length: Math.floor((W * H) / 30000) + 18 }, () => ({
        x: Math.random() * W, y: Math.random() * H, r: Math.random() * 22 + 6,
        speed: Math.random() * 0.45 + 0.15, drift: (Math.random() - 0.5) * 0.25
      }));
      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        bubbles.forEach(b => {
          ctx.beginPath(); ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          ctx.fillStyle = rgba(0.12); ctx.strokeStyle = rgba(0.55); ctx.lineWidth = 1.2;
          ctx.fill(); ctx.stroke(); b.y -= b.speed; b.x += b.drift;
          if (b.y < -b.r) { b.y = H + b.r; b.x = Math.random() * W; }
        });
        animRef.current = requestAnimationFrame(draw);
      };
      animRef.current = requestAnimationFrame(draw);
    }

    // ── Light rain ───────────────────────────────────────────────────────────
    if (type === "rain") {
      const drops = Array.from({ length: Math.floor((W * H) / 12000) + 35 }, () => ({
        x: Math.random() * W, y: Math.random() * H, len: Math.random() * 18 + 8, speed: Math.random() * 3 + 2
      }));
      const draw = () => {
        ctx.clearRect(0, 0, W, H); ctx.lineWidth = 1.2; ctx.strokeStyle = rgba(0.55);
        drops.forEach(d => {
          ctx.beginPath(); ctx.moveTo(d.x, d.y); ctx.lineTo(d.x - d.len * 0.35, d.y + d.len); ctx.stroke();
          d.x -= d.speed * 0.35; d.y += d.speed;
          if (d.y > H + d.len || d.x < -d.len) { d.x = Math.random() * W + W * 0.2; d.y = -d.len; }
        });
        animRef.current = requestAnimationFrame(draw);
      };
      animRef.current = requestAnimationFrame(draw);
    }

    // ── Orbits ───────────────────────────────────────────────────────────────
    if (type === "orbits") {
      const rings = Array.from({ length: 8 }, (_, index) => ({
        x: Math.random() * W, y: Math.random() * H, r: 35 + index * 18 + Math.random() * 20, phase: Math.random() * Math.PI * 2, speed: 0.004 + Math.random() * 0.006
      }));
      const draw = () => {
        ctx.clearRect(0, 0, W, H);
        rings.forEach(ring => {
          ctx.beginPath(); ctx.arc(ring.x, ring.y, ring.r, 0, Math.PI * 2); ctx.strokeStyle = rgba(0.22); ctx.lineWidth = 1; ctx.stroke();
          ring.phase += ring.speed; const px = ring.x + Math.cos(ring.phase) * ring.r; const py = ring.y + Math.sin(ring.phase) * ring.r;
          ctx.beginPath(); ctx.arc(px, py, 2.8, 0, Math.PI * 2); ctx.fillStyle = rgba(0.8); ctx.fill();
        });
        animRef.current = requestAnimationFrame(draw);
      };
      animRef.current = requestAnimationFrame(draw);
    }

    return () => {
      window.removeEventListener('resize', resize);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [color, opacity]);

  useEffect(() => {
    if (effect === 'none' || effect === 'modern' || THEMED_EFFECTS[effect]) return;
    const canvas = canvasRef.current;
    const cleanup = startCanvas(canvas, effect);
    return () => {
      if (cleanup) cleanup();
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [effect, darkMode, color, opacity, startCanvas]);

  if (!effect || effect === 'none') return null;

  const posClass = contained ? 'absolute inset-0' : 'fixed inset-0';

  // ── Themed line-art fields ────────────────────────────────────────────────
  if (THEMED_EFFECTS[effect]) {
    const icons = THEMED_EFFECTS[effect];
    const themedIntensity = Math.min(1, Math.max(0.3, Number(opacity || 60) / 100));
    const items = Array.from({ length: contained ? 14 : 24 }, (_, index) => ({
      Icon: icons[index % icons.length],
      left: (index * 37 + 7) % 94,
      top: (index * 53 + 11) % 92,
      size: 22 + ((index * 17) % 34),
      duration: 10 + (index % 7) * 2.2,
      delay: -((index * 1.9) % 13),
      rotation: -22 + ((index * 29) % 45),
      animation: `themedFloat${(index % 3) + 1}`,
      alpha: (0.3 + (index % 4) * 0.07) * themedIntensity,
    }));

    return (
      <>
        <style>{`
          @keyframes themedFloat1 { 0%,100%{transform:translate3d(0,0,0) rotate(var(--rotate)) scale(.82)} 45%{transform:translate3d(18px,-28px,0) rotate(calc(var(--rotate) + 14deg)) scale(1.12)} 70%{transform:translate3d(-10px,-12px,0) rotate(calc(var(--rotate) - 8deg)) scale(.96)} }
          @keyframes themedFloat2 { 0%,100%{transform:translate3d(0,0,0) rotate(var(--rotate)) scale(1.08)} 35%{transform:translate3d(-24px,16px,0) rotate(calc(var(--rotate) - 16deg)) scale(.84)} 72%{transform:translate3d(12px,-24px,0) rotate(calc(var(--rotate) + 9deg)) scale(1.18)} }
          @keyframes themedFloat3 { 0%,100%{transform:translate3d(0,0,0) rotate(var(--rotate)) scale(.9)} 30%{transform:translate3d(10px,25px,0) rotate(calc(var(--rotate) + 10deg)) scale(1.16)} 68%{transform:translate3d(-18px,-18px,0) rotate(calc(var(--rotate) - 12deg)) scale(.8)} }
        `}</style>
        <div className={`${posClass} z-0 pointer-events-none overflow-hidden`} aria-hidden="true">
          {items.map(({ Icon, ...item }, index) => (
            <Icon key={`${effect}-${index}`} strokeWidth={1.35} style={{
              position: "absolute", left: `${item.left}%`, top: `${item.top}%`,
              width: item.size, height: item.size, color, opacity: item.alpha,
              "--rotate": `${item.rotation}deg`,
              animation: `${item.animation} ${item.duration}s ease-in-out ${item.delay}s infinite`,
              filter: `drop-shadow(0 2px 5px rgba(0,0,0,${0.06 * themedIntensity}))`,
            }} />
          ))}
        </div>
      </>
    );
  }

  // ── Modern — CSS gradient blobs (no canvas) ──────────────────────────────
  if (effect === 'modern') {
    const modernRgb = hexToRgb(color).join(",");
    const modernIntensity = Math.min(1, Math.max(0.3, Number(opacity || 60) / 100));
    const blobs = [
      { color: `rgba(${modernRgb},${0.22 * modernIntensity})`, top: "5%", left: "10%", w: "50vw", h: "45vw" },
      { color: `rgba(${modernRgb},${0.16 * modernIntensity})`, top: "55%", left: "55%", w: "42vw", h: "37vw" },
      { color: `rgba(${modernRgb},${0.11 * modernIntensity})`, top: "72%", left: "8%", w: "35vw", h: "30vw" },
    ];

    return (
      <>
        <style>{`
          @keyframes bgBlob1 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(4%,3%) scale(1.05)} 66%{transform:translate(-3%,4%) scale(0.97)} }
          @keyframes bgBlob2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-5%,2%) scale(1.04)} 66%{transform:translate(3%,-4%) scale(1.02)} }
          @keyframes bgBlob3 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(3%,-3%) scale(0.96)} 66%{transform:translate(-4%,2%) scale(1.06)} }
        `}</style>
        <div className={`${posClass} z-0 pointer-events-none overflow-hidden`} aria-hidden="true">
          {blobs.map((b, i) => (
            <div key={i} style={{
              position: 'absolute',
              top: b.top, left: b.left,
              width: b.w, height: b.h,
              borderRadius: '50%',
              background: b.color,
              filter: 'blur(60px)',
              animation: `bgBlob${i + 1} ${16 + i * 4}s ease-in-out infinite`,
              transform: 'translate(-50%, -50%)',
            }} />
          ))}
        </div>
      </>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={`${posClass} z-0 pointer-events-none`}
      style={{ width: '100%', height: '100%' }}
      aria-hidden="true"
    />
  );
}
